import { debug as debugGen } from "debug";
import { UserActionReportReq, UserActionReportRsp } from "../actionTypes";
import { Action, ActionResult } from "../types";
import config from "../config";
import Queue from "./Queue";

const debug = debugGen("vtTracker:XHRTransport");

const REPORT_URL = "https://h5.qzone.qq.com/report/action";
const QUEUE_TIME = 100;

interface CacheAction {
    action: Action;
    resolver: (rsp: ActionResult<UserActionReportRsp>) => void;
}

export default class XHRTransport {
    private queue: Queue<CacheAction>;

    constructor() {
        this.queue = new Queue(QUEUE_TIME, this.onTime.bind(this));
    }

    onTime(actions: Array<CacheAction>): void {
        const typesMap: { [K: string]: Array<CacheAction> } = {};

        actions.forEach(ca => {
            const action: UserActionReportReq = JSON.parse(ca.action.content);

            typesMap[action.type] = [...(typesMap[action.type] || []), ca];
        });

        Object.entries(typesMap).forEach((it: [string, Array<CacheAction>]) => {
            this.sendSameType(it[1]);
        });
    }

    sendSameType(actions: Array<CacheAction>): void {
        const req: UserActionReportReq = {
            type: 0,
            user_comm_report: null,
            report_infos: []
        };

        actions.forEach(ca => {
            const action: UserActionReportReq = JSON.parse(ca.action.content);

            if (req.user_comm_report === null) {
                req.user_comm_report = action.user_comm_report;
                req.type = action.type;
            }

            req.report_infos = req.report_infos.concat(action.report_infos);
        });

        const reqStr: string = JSON.stringify(req);

        debug("onTime report: %s", reqStr);

        if (
            config.useBeacon &&
            window.navigator.sendBeacon &&
            this.sendBeacon(reqStr) === true
        ) {
            actions.forEach(ca => {
                ca.resolver({
                    action: ca.action,
                    result: {
                        ret_code: 0,
                        ret_msg: "",
                        trace_details: []
                    }
                });
            });
        } else {
            this.fetch(reqStr).then(result => {
                actions.forEach((ca, idx) => {
                    ca.resolver({
                        action: ca.action,
                        result: {
                            ret_code: result.ret_code,
                            ret_msg: result.ret_msg,
                            trace_details:
                                result.trace_details &&
                                result.trace_details[idx]
                                    ? [result.trace_details[idx]]
                                    : []
                        }
                    });
                });
            });
        }
    }

    async send(action: Action): Promise<ActionResult<UserActionReportRsp>> {
        if (action.immediate === true) {
            return {
                action,
                result: await this.fetch(action.content)
            };
        }

        const promise: Promise<ActionResult<UserActionReportRsp>> = new Promise(
            resolve => {
                this.queue.push({
                    action,
                    resolver: resolve
                });
            }
        );

        return promise;
    }

    private reportUrl(query?: { [Key: string]: string }): string {
        const url: URL = new URL(config.reportUrl || REPORT_URL);

        if (config.debugUin) {
            url.searchParams.set("uin", config.debugUin);
        }

        if (config.busiVersion) {
            url.searchParams.set("busiversion", config.busiVersion);
        }

        Object.entries(query || {}).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });

        return url.href;
    }

    sendBeacon(content: string): boolean {
        return window.navigator.sendBeacon(this.reportUrl(), content);
    }

    async fetch(content: string): Promise<UserActionReportRsp> {
        const result: UserActionReportRsp = await fetch(this.reportUrl(), {
            method: "POST",
            credentials: "omit",
            body: content
        })
            .then((d: Response) => {
                return d.json();
            })
            .then((d = {}) => {
                return d.data || {};
            })
            .catch(() => {
                return {};
            });

        return result;
    }
}
